/**
 * Ball-position derivation for the AFL field UI.
 *
 * Until the engine tracks actual ball position on the field (planned
 * with the corridor/wing zone model), the ball sits at centre during
 * any phase except an active marking contest. Don't pretend possession
 * implies forward-50 placement — the ball only moves when something
 * has actually kicked, handballed, or carried it.
 */

export type BallZone =
  | 'centre'
  | 'left_50'
  | 'right_50'
  | 'left_half'
  | 'right_half';

export type BallPosition = {
  x: number;
  y: number;
  zone: BallZone;
};

type AflContestSnapshot = {
  kind: string;
  attackerSide: { seatId: string };
} | null;

type AflViewerSnapshot = {
  phase: string;
  possessionSeatId: string | null;
  contest: AflContestSnapshot;
};

export function computeBallPosition(
  view: AflViewerSnapshot,
  rightSeatId: string,
  leftSeatId: string,
): BallPosition {
  const contest = view.contest;

  // Marking contest still gives us a directional hint — the attacker
  // is going for the mark deep in the opponent's defensive 50.
  if (contest?.kind === 'marking_contest') {
    const attacker = contest.attackerSide.seatId;
    if (attacker === rightSeatId) {
      return { x: 22, y: 50, zone: 'left_50' };
    }
    if (attacker === leftSeatId) {
      return { x: 78, y: 50, zone: 'right_50' };
    }
  }

  // Everything else — ruck, clearance, open play with no real position
  // data — sits at centre. The field is not lying about where the ball
  // is when nobody has actually moved it.
  return { x: 50, y: 50, zone: 'centre' };
}
